import { useContext, useEffect, useRef, useState } from "react";
import { CanvasContext } from "../ResizingCanvas";
import { getViewportCanvas } from "../../utils";
import { useLoader } from "@react-three/fiber";
import {
  Mesh,
  PlaneGeometry,
  DoubleSide,
  TextureLoader,
  MeshBasicMaterial,
} from "three";
import { useControls } from "leva";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

const CurvedMesh = () => {
  const { sizes, camera } = useContext(CanvasContext); // canvas context
  const [vertices, setVertices] = useState<number[]>(); // plane vertices
  const meshRef = useRef<Mesh>(null);
  const materialRef = useRef<MeshBasicMaterial>(null);
  const radius = 10;
  const segments = 70;

  /**
   * Plane Viewport Size
   */
  const { viewportWidth, viewportHeight } = getViewportCanvas(
    camera.fov,
    camera.position.z,
    sizes.aspect
  );

  /**
   * Debug
   */
  // const { radius, segments } = useControls({
  //   radius: 10,
  //   segments: 70,
  // });

  /**
   * Texture
   */
  const map = useLoader(TextureLoader, "assets/images/architecture.jpg");

  useEffect(() => {
    if (materialRef.current) {
      const texture = materialRef.current.map!;
      const textureAspect = texture.image.width / texture.image.height;
      const textureAspectH = texture.image.height / texture.image.width;
      const aspectH = sizes.height / sizes.width;

      // Texture repeat responsive value
      if (textureAspect < sizes.aspect) {
        texture.repeat.set(1, aspectH / textureAspectH);
      } else {
        texture.repeat.set(sizes.aspect / textureAspect, 1);
      }
    }
  }, [sizes]);

  /**
   * Get Plane Vertices
   */
  useEffect(() => {
    const plane = new PlaneGeometry(viewportWidth, viewportHeight, segments, 1);
    const position = plane.attributes.position;
    const newArray = [];

    for (let i = 0; i < position.count; i++) {
      const i3 = i * 3;
      newArray[i3] = position.array[i3];
      newArray[i3 + 1] = position.array[i3 + 1];
      newArray[i3 + 2] = radius;
    }

    setVertices(newArray);
  }, [segments, radius, viewportWidth, viewportHeight]);

  /**
   * Curved ScrollTrigger
   */
  useGSAP(() => {
    if (meshRef.current && vertices) {
      const { geometry } = meshRef.current;
      const { position } = geometry.attributes;
      meshRef.current.position.setZ(radius * -1);

      gsap.to(position.array, {
        endArray: vertices,
        scrollTrigger: {
          trigger: "#canvas-curved",
          start: "top 60%",
          end: "top top",
          scrub: 1,
        },
        onUpdate: () => {
          position.needsUpdate = true;
        },
      });
    }
  }, [radius, vertices]);

  return (
    <mesh ref={meshRef} position={[0, 0, 0]}>
      <cylinderGeometry
        args={[
          radius, // radiusTop
          radius, // radiusBottom
          viewportHeight, // height
          segments, // radialSegments
          1, // heightSegments
          true, // openEnded
          Math.PI / -2, // thetaStart
          Math.PI, // thetaLength
        ]}
      />
      <meshBasicMaterial
        ref={materialRef}
        color="#ffffff"
        side={DoubleSide}
        map={map}
      />
    </mesh>
  );
};

export default CurvedMesh;
